import { useCallback, useEffect, useMemo, useState } from 'react'
import { styled } from '@mui/material/styles'
import Modal from '@mui/material/Modal'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import FormGroup from '@mui/material/FormGroup'
import Typography from '@mui/material/Typography'
import {
  useNavigate,
  useParams,
  useLoaderData,
  useRouteLoaderData,
  useSubmit,
  useActionData,
} from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Decimal from 'decimal.js'
import cloneDeep from 'lodash/cloneDeep'

import EventInfoBox from '../components/EventInfoBox'
import ParticipantStatusTag from '../components/ParticipantStatusTag'
import { LoaderData, ParticipantStatus, OptionPathKeys } from '../../../types'
import type { eventLoader } from '../Events.loader'
import type { eventRegistrationDetailsLoader } from './RegisterEvents.loader'
import type { registerEvent } from './RegisterEvents.action'
import {
  RegistrationOptions,
  useFilterRegistrationOptions,
} from './components/filterRegistrationOptions'
import CheckBoxLine from './components/CheckBoxLine'
import InformationLine from './components/InformationLine'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: { xs: 0, sm: 6 },
  boxShadow: 24,
  padding: { xs: 1, sm: 3 },
  width: { xs: '100%', sm: '90%' },
  height: { xs: '100%', sm: '80%' },
  overflow: 'auto',
}

const Title = styled('h2')(() => ({
  marginBottom: 0,
}))
const Subtitle = styled('h4')(() => ({
  marginBottom: 0,
}))
const Span = styled('span')(({ theme }) => ({
  lineHeight: 1,
  color: theme.palette.text.secondary,
  margin: '0.5rem 0',
}))
const Description = styled('span')(({ theme }) => ({
  color: theme.palette.text.primary,
}))
const Price = styled('span')(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.text.primary,
  display: 'block',
  width: 'calc(100% - 1rem)',
  textAlign: 'right',
}))
const Form = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'space-between',
  flex: '1 0 auto',
}))

const calculatePricePerLine = (options: Map<number, RegistrationOptions>) => {
  const values = Array.from(options.values())
  return values.reduce((acc, e) => acc.add(new Decimal(e.price)), new Decimal(0)).toFixed(2)
}

export default function RegisterEvents() {
  const { t } = useTranslation('events')
  const navigate = useNavigate()
  const submit = useSubmit()
  const actionData = useActionData() as LoaderData<ReturnType<typeof registerEvent>> | undefined
  const [loading, setLoading] = useState(false)

  const { eventId } = useParams()
  if (!eventId) {
    throw new Error('eventId is required')
  }

  const registrationDetails = useLoaderData() as LoaderData<
    ReturnType<typeof eventRegistrationDetailsLoader>
  >
  const data = useRouteLoaderData('view-event') as LoaderData<ReturnType<typeof eventLoader>>

  const [selectedOptions, setSelectedOptions] = useState<
    Record<OptionPathKeys, Map<number, RegistrationOptions>>
  >({
    mealOptions: new Map(),
    hotelOptions: new Map(),
    workshopOptions: new Map(),
    subEventOptions: new Map(),
    sessionOptions: new Map(),
    textOptions: new Map(),
    numberOptions: new Map(),
    checkboxOptions: new Map(),
  })

  const setOptionsByType = useCallback(
    (path: OptionPathKeys, value: string, elem: RegistrationOptions) => {
      const isCheckboxType =
        path === 'checkboxOptions' ||
        path === 'hotelOptions' ||
        path === 'mealOptions' ||
        path === 'workshopOptions' ||
        path === 'subEventOptions' ||
        path === 'sessionOptions'

      if (isCheckboxType && value === 'false') {
        setSelectedOptions(prev => {
          prev[path].delete(elem.option.id)
          return cloneDeep(prev)
        })
      } else {
        setSelectedOptions(prev => {
          prev[path].set(elem.option.id, { ...elem, value })
          return cloneDeep(prev)
        })
      }
    },
    []
  )

  const registrationOptions = useFilterRegistrationOptions(
    registrationDetails,
    data?.subEvents,
    data?.sessions,
    setOptionsByType
  )

  const handleClose = () => {
    navigate(`/events/${eventId}`)
  }

  const hotelPrice = useMemo(
    () => calculatePricePerLine(selectedOptions.hotelOptions),
    [selectedOptions.hotelOptions]
  )
  const mealPrice = useMemo(
    () => calculatePricePerLine(selectedOptions.mealOptions),
    [selectedOptions.mealOptions]
  )
  const workshopPrice = useMemo(
    () => calculatePricePerLine(selectedOptions.workshopOptions),
    [selectedOptions.workshopOptions]
  )
  const subEventPrice = useMemo(
    () => calculatePricePerLine(selectedOptions.subEventOptions),
    [selectedOptions.subEventOptions]
  )
  const sessionPrice = useMemo(
    () => calculatePricePerLine(selectedOptions.sessionOptions),
    [selectedOptions.sessionOptions]
  )
  const restPrice = useMemo(
    () =>
      calculatePricePerLine(
        new Map([
          ...selectedOptions.textOptions,
          ...selectedOptions.numberOptions,
          ...selectedOptions.checkboxOptions,
        ])
      ),
    [selectedOptions.textOptions, selectedOptions.numberOptions, selectedOptions.checkboxOptions]
  )
  const total = useMemo(
    () =>
      new Decimal(registrationDetails?.event_price ?? 0)
        .add(new Decimal(mealPrice))
        .add(new Decimal(workshopPrice))
        .add(new Decimal(hotelPrice))
        .add(new Decimal(subEventPrice))
        .add(new Decimal(sessionPrice))
        .add(new Decimal(restPrice))
        .add(new Decimal(registrationDetails?.deposit_price ?? 0))
        .add(
          new Decimal(
            selectedOptions.hotelOptions.size ? (registrationDetails?.hotel_deposit_price ?? 0) : 0
          )
        )
        .toFixed(2),
    [
      hotelPrice,
      mealPrice,
      workshopPrice,
      subEventPrice,
      sessionPrice,
      restPrice,
      registrationDetails?.event_price,
      registrationDetails?.deposit_price,
      registrationDetails?.hotel_deposit_price,
      selectedOptions.hotelOptions.size,
    ]
  )

  const onSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const hotelOptions = Array.from(selectedOptions.hotelOptions.values()).map(e => ({
      option_id: e.option.id,
      value: '',
    }))
    const mealOptions = Array.from(selectedOptions.mealOptions.values()).map(e => ({
      option_id: e.option.id,
      value: '',
    }))
    const workshopOptions = Array.from(selectedOptions.workshopOptions.values()).map(e => ({
      option_id: e.option.id,
      value: '',
    }))
    const subEventOptions = Array.from(selectedOptions.subEventOptions.values()).map(e => ({
      option_id: e.option.id,
      value: '',
    }))
    const sessionOptions = Array.from(selectedOptions.sessionOptions.values()).map(e => ({
      option_id: e.option.id,
      value: '',
    }))
    const textOptions = Array.from(selectedOptions.textOptions.values()).map(e => ({
      option_id: e.option.id,
      value: e.value ?? null,
    }))
    const numberOptions = Array.from(selectedOptions.numberOptions.values()).map(e => ({
      option_id: e.option.id,
      value: e.value ?? null,
    }))
    const checkboxOptions = Array.from(selectedOptions.checkboxOptions.values()).map(e => ({
      option_id: e.option.id,
      value: '',
    }))

    submit(
      {
        event_id: eventId,
        options: [
          ...hotelOptions,
          ...mealOptions,
          ...workshopOptions,
          ...subEventOptions,
          ...sessionOptions,
          ...textOptions,
          ...numberOptions,
          ...checkboxOptions,
        ],
      },
      {
        method: 'POST',
        encType: 'application/json',
        action: `/events/${eventId}/register`,
      }
    )
    setLoading(true)
  }

  useEffect(() => {
    if (actionData) {
      setLoading(false)
    }
  }, [actionData, eventId, navigate])

  return (
    <Modal open onClose={handleClose}>
      <Stack sx={style}>
        <Form onSubmit={onSubmitForm}>
          <Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap="1rem"
              width="-webkit-fill-available"
            >
              <Title>
                {registrationDetails?.existing_registration
                  ? t("Détails de l'inscription")
                  : t("S'inscrire à l'événement")}
              </Title>
              <ParticipantStatusTag status={data?.event?.registration_status} />
            </Stack>
            <Divider />
            <Stack margin="0.5rem 1rem">
              <Subtitle>{data?.event?.name}</Subtitle>
              <Divider />
              <EventInfoBox
                start={data?.event?.start}
                end={data?.event?.end}
                location={data?.event?.location}
                margin="0.6rem 0"
              />

              {!!registrationDetails?.deposit_price &&
              Number(registrationDetails.deposit_price) !== 0 ? (
                <Span
                  sx={theme => ({
                    color: theme.palette.error.main,
                  })}
                >
                  {t("Une caution sera demandée pour la réservation de l'événement")} :{' '}
                  {registrationDetails.deposit_price}€
                  <br />
                  {t(
                    "La caution versée implique un engagement sur l'ensemble des options sélectionnées."
                  )}
                </Span>
              ) : null}

              {!!registrationOptions?.sessionOptions?.length && (
                <>
                  <Subtitle>
                    {registrationDetails?.existing_registration
                      ? t('Les séances')
                      : t('Sélectionner les séances')}
                  </Subtitle>
                  <Divider />
                  <FormGroup sx={{ marginLeft: '1rem', flexWrap: 'nowrap' }}>
                    {registrationOptions.sessionOptions.map(elem => (
                      <CheckBoxLine
                        key={elem.option.id}
                        elem={elem}
                        path="sessionOptions"
                        setOptionsByType={setOptionsByType}
                        registrationDetails={registrationDetails}
                      />
                    ))}
                  </FormGroup>
                </>
              )}

              {!!registrationOptions?.workshopOptions?.length && (
                <>
                  <Subtitle>
                    {registrationDetails?.existing_registration
                      ? t('Les ateliers')
                      : t('Sélectionner les ateliers')}
                  </Subtitle>
                  <Divider />
                  <FormGroup sx={{ marginLeft: '1rem', flexWrap: 'nowrap' }}>
                    {registrationOptions.workshopOptions.map(elem => (
                      <CheckBoxLine
                        key={elem.option.id}
                        elem={elem}
                        path="workshopOptions"
                        setOptionsByType={setOptionsByType}
                        registrationDetails={registrationDetails}
                      />
                    ))}
                  </FormGroup>
                </>
              )}

              {!!registrationOptions?.subEventOptions?.length && (
                <>
                  <Subtitle>
                    {registrationDetails?.existing_registration
                      ? t('Les inscriptions complémentaires')
                      : t('Sélectionner les inscriptions complémentaires')}
                  </Subtitle>
                  <Divider />
                  <FormGroup sx={{ marginLeft: '1rem', flexWrap: 'nowrap' }}>
                    {registrationOptions.subEventOptions.map(elem => (
                      <CheckBoxLine
                        key={elem.option.id}
                        elem={elem}
                        path="subEventOptions"
                        setOptionsByType={setOptionsByType}
                        registrationDetails={registrationDetails}
                      />
                    ))}
                  </FormGroup>
                </>
              )}

              {!!registrationOptions?.hotelOptions?.length && (
                <>
                  <Subtitle>
                    {registrationDetails?.existing_registration
                      ? t('Les hébergements')
                      : t('Sélectionner les hébergements')}
                  </Subtitle>
                  <Divider />
                  <FormGroup sx={{ marginLeft: '1rem', flexWrap: 'nowrap' }}>
                    {registrationOptions.hotelOptions.map(elem => (
                      <CheckBoxLine
                        key={elem.option.id}
                        elem={elem}
                        path="hotelOptions"
                        setOptionsByType={setOptionsByType}
                        registrationDetails={registrationDetails}
                      />
                    ))}
                  </FormGroup>
                  {registrationOptions.hotelOptions.length &&
                  Number(registrationDetails?.hotel_deposit_price) !== 0 ? (
                    <Span>
                      {t("Une caution sera demandée pour la réservation de l'hôtel")} :{' '}
                      {registrationDetails?.hotel_deposit_price}€
                    </Span>
                  ) : null}
                </>
              )}

              {!!registrationOptions?.mealOptions?.length && (
                <>
                  <Subtitle>
                    {registrationDetails?.existing_registration
                      ? t('Les repas')
                      : t('Sélectionner les repas')}
                  </Subtitle>
                  <Divider />
                  <FormGroup sx={{ marginLeft: '1rem', flexWrap: 'nowrap' }}>
                    {registrationOptions.mealOptions.map(elem => (
                      <CheckBoxLine
                        key={elem.option.id}
                        elem={elem}
                        path="mealOptions"
                        setOptionsByType={setOptionsByType}
                        registrationDetails={registrationDetails}
                      />
                    ))}
                  </FormGroup>
                </>
              )}

              {!!(
                registrationOptions?.textOptions?.length ||
                registrationOptions?.numberOptions?.length ||
                registrationOptions?.checkboxOptions?.length
              ) && (
                <>
                  <Subtitle>
                    {registrationDetails?.existing_registration
                      ? t('Les informations complémentaires')
                      : t('Renseignez les informations complémentaires')}
                  </Subtitle>
                  <Divider />
                  <FormGroup sx={{ marginLeft: '1rem', flexWrap: 'nowrap' }}>
                    {registrationOptions?.textOptions.map(elem => (
                      <InformationLine
                        key={elem.option.id}
                        elem={elem}
                        path="textOptions"
                        setOptionsByType={setOptionsByType}
                        registrationDetails={registrationDetails}
                      />
                    ))}
                    {registrationOptions?.numberOptions.map(elem => (
                      <InformationLine
                        key={elem.option.id}
                        elem={elem}
                        path="numberOptions"
                        setOptionsByType={setOptionsByType}
                        registrationDetails={registrationDetails}
                      />
                    ))}
                    {registrationOptions?.checkboxOptions.map(elem => (
                      <CheckBoxLine
                        key={elem.option.id}
                        elem={elem}
                        path="checkboxOptions"
                        setOptionsByType={setOptionsByType}
                        registrationDetails={registrationDetails}
                        hideFreePrice
                      />
                    ))}
                  </FormGroup>
                </>
              )}
            </Stack>

            {registrationDetails?.existing_registration?.status ===
            ParticipantStatus.Confirmed ? null : (
              <Grid
                container
                spacing={2}
                margin="0.5rem auto 2rem"
                maxWidth="25rem"
                paddingBottom="1rem"
                sx={theme => ({
                  borderRadius: '0.5rem',
                  backgroundColor: theme.colors.secondaryBackground,
                })}
              >
                <Grid item xs={8}>
                  <Description>{t('Prix événement')}:</Description>
                </Grid>
                <Grid item xs={4}>
                  <Price>
                    {Number(registrationDetails?.event_price)
                      ? `${registrationDetails?.event_price} €`
                      : t('Inclus')}
                  </Price>
                </Grid>
                {registrationDetails?.deposit_price &&
                Number(registrationDetails.deposit_price) !== 0 ? (
                  <>
                    <Grid item xs={8}>
                      <Description>{t('Caution')}:</Description>
                    </Grid>
                    <Grid item xs={4}>
                      <Price>{registrationDetails.deposit_price} €</Price>
                    </Grid>
                  </>
                ) : null}
                {selectedOptions.hotelOptions.size ? (
                  <>
                    <Grid item xs={8}>
                      <Description>{t('Prix hôtel')}:</Description>
                    </Grid>
                    <Grid item xs={4}>
                      <Price>{Number(hotelPrice) ? `${hotelPrice} €` : t('Inclus')}</Price>
                    </Grid>
                    {Number(registrationDetails?.hotel_deposit_price) !== 0 ? (
                      <>
                        <Grid item xs={8}>
                          <Description>{t('Caution hôtel')}:</Description>
                        </Grid>
                        <Grid item xs={4}>
                          <Price>{registrationDetails?.hotel_deposit_price} €</Price>
                        </Grid>
                      </>
                    ) : null}
                  </>
                ) : null}
                {selectedOptions.mealOptions.size ? (
                  <>
                    <Grid item xs={8}>
                      <Description>{t('Prix repas')}:</Description>
                    </Grid>
                    <Grid item xs={4}>
                      <Price>{Number(mealPrice) ? `${mealPrice} €` : t('Inclus')}</Price>
                    </Grid>
                  </>
                ) : null}
                {selectedOptions.sessionOptions.size ? (
                  <>
                    <Grid item xs={8}>
                      <Description>{t('Prix séances')}:</Description>
                    </Grid>
                    <Grid item xs={4}>
                      <Price>{Number(sessionPrice) ? `${sessionPrice} €` : t('Inclus')}</Price>
                    </Grid>
                  </>
                ) : null}
                {selectedOptions.workshopOptions.size ? (
                  <>
                    <Grid item xs={8}>
                      <Description>{t('Prix ateliers')}:</Description>
                    </Grid>
                    <Grid item xs={4}>
                      <Price>{Number(workshopPrice) ? `${workshopPrice} €` : t('Inclus')}</Price>
                    </Grid>
                  </>
                ) : null}
                {selectedOptions.subEventOptions.size ? (
                  <>
                    <Grid item xs={8}>
                      <Description>{t('Prix inscriptions complémentaires')}:</Description>
                    </Grid>
                    <Grid item xs={4}>
                      <Price>{Number(subEventPrice) ? `${subEventPrice} €` : t('Inclus')}</Price>
                    </Grid>
                  </>
                ) : null}
                {Number(restPrice) ? (
                  <>
                    <Grid item xs={8}>
                      <Description>{t('Prix autres prestations')}:</Description>
                    </Grid>
                    <Grid item xs={4}>
                      <Price>{`${restPrice} €`}</Price>
                    </Grid>
                  </>
                ) : null}

                <Grid item xs={8} borderTop={theme => `2px solid ${theme.palette.divider}`}>
                  <Description sx={{ fontWeight: 'bold' }}>{t('TOTAL')}:</Description>
                </Grid>
                <Grid item xs={4} borderTop={theme => `2px solid ${theme.palette.divider}`}>
                  <Price>{Number(total) ? `${total} €` : t('Inclus')}</Price>
                </Grid>
              </Grid>
            )}

            {!registrationDetails?.is_member && (
              <Stack alignItems="center" gap="1rem">
                <Typography variant="h5" color="error" maxWidth="40rem" textAlign="center">
                  {`${t("Ceci est un tarif non membre, vous pouvez mettre à jour votre adhésion pour bénéficier d'un tarif membre plus avantageux")}.`}
                </Typography>
                <Button variant="contained" href="https://www.sfctcv.org/espace-personnel-sfctcv/">
                  {t('Régler ma cotisation')}
                </Button>
              </Stack>
            )}
          </Stack>
          <Stack direction="row" justifyContent="space-between" paddingBottom="1.5rem">
            <Button
              onClick={e => {
                e.preventDefault()
                handleClose()
              }}
            >
              {registrationDetails?.existing_registration?.status === ParticipantStatus.Confirmed
                ? t('Fermer')
                : t('Annuler')}
            </Button>
            <LoadingButton
              disabled={
                registrationDetails?.existing_registration?.status === ParticipantStatus.Confirmed
              }
              variant={
                registrationDetails?.existing_registration?.status === ParticipantStatus.Confirmed
                  ? undefined
                  : 'contained'
              }
              type="submit"
              loading={loading}
            >
              {registrationDetails?.existing_registration
                ? t("Poursuivre l'inscription")
                : t("S'inscrire")}
            </LoadingButton>
          </Stack>
        </Form>
      </Stack>
    </Modal>
  )
}
